import { createTaskRunner } from '@gonfalon/async/createTaskRunner';
import { chunk } from '@gonfalon/collections/chunk';
import { searchContexts } from '@gonfalon/openapi/operations/searchContexts';
import type { schemas } from '@gonfalon/openapi/types';
import { anyOf, toString as queryfilterToString } from '@gonfalon/queryfilter/builder';
import { parse as queryfilterFromString } from '@gonfalon/queryfilter/parser';

import { reactQueryResponseAdapter } from './reactQueryResponseAdapter';

export type ContextKindKeyTask = {
  projectKey: string;
  environmentKey: string;
  query: {
    filter: string;
    sort?: string;
    limit?: number;
    continuationToken?: string;
  };
};

// this is the max results the API will return for a single batch
const MAX_RESULTS_PER_BATCH = 50;

export const queryByContextKindKeysTaskRunner = createTaskRunner<
  ContextKindKeyTask,
  schemas['Contexts'],
  NonNullable<Awaited<ReturnType<typeof searchContexts>>['data']>
>({
  runner: async (inputs) => {
    return reactQueryResponseAdapter(
      searchContexts({
        projectKey: inputs[0].projectKey,
        environmentKey: inputs[0].environmentKey,
        query: {
          filter: inputs[0].query.filter,
          sort: inputs[0].query.sort ?? '-ts',
        },
        body: {}, // Add required empty body parameter
      }),
    );
  },
  resolver: (data) => {
    return {
      _links: data._links,
      _environmentId: data._environmentId,
      continuationToken: data.continuationToken,
      totalCount: data.totalCount,
      items: data.items,
    };
  },
  batching: {
    batcher: queryByContextKindKeysTaskBatcher,
    resultMerger: (results) => {
      return {
        _links: results[0]?._links,
        _environmentId: results[0]?._environmentId,
        items: results.flatMap((result) => result.items || []),
        totalCount: results.reduce((total, result) => total + (result.totalCount ?? 0), 0),
        continuationToken: results[results.length - 1]?.continuationToken,
      };
    },
  },
});

export function queryByContextKindKeysTaskBatcher(inputs: ContextKindKeyTask[]) {
  const kindKeys = inputs.flatMap((input) => {
    const parsedFilter = queryfilterFromString(input.query.filter);
    if (parsedFilter.err) {
      return [];
    }

    if (
      parsedFilter.val.type === 'match' &&
      parsedFilter.val.matchoperator === 'anyOf' &&
      parsedFilter.val.matchname === 'kindkey'
    ) {
      return parsedFilter.val.matchvalue;
    }
    return [];
  });

  const uniqueKindKeys = Array.from(new Set(kindKeys));
  const kindKeyBatches = chunk(uniqueKindKeys, MAX_RESULTS_PER_BATCH);

  // Create a batch task for each chunk of kind keys
  return kindKeyBatches.map((keyBatch) => [
    {
      projectKey: inputs[0].projectKey,
      environmentKey: inputs[0].environmentKey,
      query: {
        filter: queryfilterToString(anyOf('kindkey', keyBatch)),
        sort: inputs[0].query.sort ?? '-ts',
        limit: inputs[0].query.limit ?? MAX_RESULTS_PER_BATCH,
      },
    },
  ]);
}
